// Etiqueta da tela de Pesagem — mesmo desenho da etiqueta que a balança
// Filizola imprime (nome, peso líquido, R$/kg, TOTAL em caixa, EAN-13 com
// Fab./Emb. e Tara ao lado). Gera HTML em mm, que qualquer impressora de
// etiqueta com driver no Windows imprime (térmica direta, 203 dpi).
//
// Impressão: pelo QZ Tray quando o computador tem uma impressora de
// etiqueta escolhida (sem diálogo, no tamanho certo); senão abre a janela
// de impressão do navegador com @page no tamanho da etiqueta.
import qz from 'qz-tray';
import { conectar } from './gaveta';
import { ean13Svg } from './ean13Svg';

const CHAVE = 'pesagem_etiqueta_cfg';
export const CFG_IMPRESSAO_PADRAO = { impressora: '', largura: 60, altura: 40, copias: 1 };

export function cfgImpressaoSalva() {
  try {
    return { ...CFG_IMPRESSAO_PADRAO, ...JSON.parse(localStorage.getItem(CHAVE) || '{}') };
  } catch (_) {
    return { ...CFG_IMPRESSAO_PADRAO };
  }
}

export function salvarCfgImpressao(cfg) {
  try { localStorage.setItem(CHAVE, JSON.stringify(cfg)); } catch (_) {}
}

const esc = s => String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const num = (v, casas) => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas });
const dataCurta = d => `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getFullYear()).slice(-2)}`;

// d: { nome, peso, tara, precoKg, total, codigo (13 dígitos), data (Date), validade (Date|null) }
export function htmlEtiqueta(d, { largura = 60, altura = 40 } = {}) {
  // Escala tudo a partir da etiqueta padrão de 60x40 mm
  const k = Math.min(largura / 60, altura / 40);
  const fs = v => `${(v * k).toFixed(2)}mm`;
  // margem branca antes das barras: o leitor precisa dela pra achar o início
  const barras = ean13Svg(d.codigo, { larguraMm: 36 * (largura / 60), alturaMm: 14 * (altura / 40) });
  const legenda = `${d.codigo[0]}&nbsp;&nbsp;${d.codigo.slice(1, 7)}&nbsp;&nbsp;&nbsp;${d.codigo.slice(7)}`;
  return `<!doctype html><html><head><meta charset="utf-8"><style>
    @page { size: ${largura}mm ${altura}mm; margin: 0; }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; }
    body { font-family: Arial, Helvetica, sans-serif; color: #000; }
    .et { width: ${largura}mm; height: ${altura}mm; padding: ${fs(1.6)} ${fs(2)}; overflow: hidden; page-break-after: always; }
    .nome { font-size: ${fs(3.6)}; font-weight: bold; text-align: center; line-height: 1.1;
            max-height: ${fs(8)}; overflow: hidden; margin-bottom: ${fs(1)}; }
    .linha { display: flex; justify-content: space-between; font-size: ${fs(2.8)}; margin-bottom: ${fs(1)}; }
    .total { border: ${fs(0.35)} solid #000; display: flex; justify-content: space-between; align-items: center;
             font-size: ${fs(4.2)}; font-weight: bold; padding: ${fs(0.4)} ${fs(1.5)}; margin-bottom: ${fs(1)}; }
    .base { display: flex; justify-content: space-between; align-items: flex-start; }
    .barras { padding-left: ${fs(1.5)}; }
    .cod { font-family: 'Courier New', monospace; font-size: ${fs(2.6)}; letter-spacing: ${fs(0.15)}; }
    .info { font-size: ${fs(2.3)}; text-align: right; line-height: 1.25; }
  </style></head><body>
  <div class="et">
    <div class="nome">${esc(d.nome)}</div>
    <div class="linha"><span>PESO LÍQ.: ${num(d.peso, 3)}kg</span><span>R$/kg&nbsp;&nbsp;${num(d.precoKg, 2)}</span></div>
    <div class="total"><span>TOTAL R$</span><span>${num(d.total, 2)}</span></div>
    <div class="base">
      <div class="barras">${barras}<div class="cod">${legenda}</div></div>
      <div class="info">Fab./Emb.:<br>${dataCurta(d.data)}<br>TARA:<br>${num(d.tara, 3)}kg${d.validade ? `<br>Val.: ${dataCurta(d.validade)}` : ''}</div>
    </div>
  </div></body></html>`;
}

// Repete o bloco da etiqueta pra imprimir várias cópias num job só
function comCopias(html, copias) {
  const n = Math.max(1, Math.min(50, parseInt(copias, 10) || 1));
  if (n === 1) return html;
  const ini = html.indexOf('<div class="et">');
  const fim = html.lastIndexOf('</body>');
  const bloco = html.slice(ini, fim);
  return html.slice(0, ini) + bloco.repeat(n) + html.slice(fim);
}

export async function listarImpressorasEtiqueta() {
  await conectar();
  return qz.printers.find();
}

// Devolve 'direta' (QZ Tray) ou 'navegador' (janela de impressão).
export async function imprimirEtiqueta(html, cfg) {
  const conteudo = comCopias(html, cfg.copias);
  if (cfg.impressora) {
    try {
      await conectar();
      const config = qz.configs.create(cfg.impressora, {
        size: { width: Number(cfg.largura), height: Number(cfg.altura) }, units: 'mm', margins: 0, scaleContent: false,
      });
      await qz.print(config, [{ type: 'pixel', format: 'html', flavor: 'plain', data: conteudo }]);
      return 'direta';
    } catch (err) {
      console.error('[Etiqueta] QZ Tray falhou, abrindo a impressão do navegador:', err);
    }
  }
  const w = window.open('', '_blank', 'width=420,height=360');
  if (!w) throw new Error('O navegador bloqueou a janela de impressão — libere pop-ups para este site.');
  w.document.write(conteudo);
  w.document.close();
  // document.write não dispara onload em todo navegador — espera o SVG desenhar
  setTimeout(() => { w.focus(); w.print(); setTimeout(() => w.close(), 500); }, 300);
  return 'navegador';
}
